import type { FC, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import type { GameV1 } from '@/api/games';
import { IconComponent } from '@/components/IconComponent';
import { ScrollBlock } from '@/components/ScrollBlock';
import type { BonusFlipItemProps } from '..';
import { useFormatBonusTerms } from './hooks';

export const BonusTerms: FC<BonusFlipItemProps & { bonusGames?: GameV1[] }> = ({
  bonus,
  bonusGames,
}) => {
  const { t } = useTranslation('bonuses');
  const {
    dateReceived,
    activateUntilDate,
    expireDate,
    wager,
    amountWager,
    wagerRequirement,
    bonusAmount,
    bonusFreespins,
    bonusGamesLinks,
    status,
    maxWin,
    spinValue,
  } = useFormatBonusTerms(bonus, bonusGames);

  const TermsPoint: FC<{
    translationKey: string;
    value: string | number;
    icon: ReactElement;
  }> = ({ translationKey, value, icon }) => (
    <li>
      {icon}
      <span className="bonus-terms">
        {t(`bonusTerms.${translationKey}`, { value })}
      </span>
    </li>
  );

  return (
    <ScrollBlock height="100%" scrollTopMargin={0} width="100%">
      <ul>
        <TermsPoint
          icon={
            <IconComponent
              className="clock-icon"
              name="clockIcon"
              size="16px"
            />
          }
          translationKey="dateReceived"
          value={dateReceived}
        />
        {activateUntilDate && (
          <TermsPoint
            icon={
              <IconComponent
                className="clock-icon"
                name="clockIcon"
                size="16px"
              />
            }
            translationKey="activateUntil"
            value={activateUntilDate}
          />
        )}
        {expireDate && (
          <TermsPoint
            icon={
              <IconComponent
                className="hourglass-icon"
                name="hourglass"
                size="20px"
              />
            }
            translationKey="expireDate"
            value={expireDate}
          />
        )}
        <TermsPoint
          icon={
            <IconComponent
              className="tooltip-icon"
              name="tooltipIcon"
              size="23px"
            />
          }
          translationKey="status"
          value={status}
        />
        {bonusGames && (
          <li>
            <IconComponent name="game" size="20px" />
            {t('bonusTerms.bonusGame')} {bonusGamesLinks}
          </li>
        )}
        {bonusFreespins && (
          <TermsPoint
            icon={<IconComponent name="fsAmount" size="20px" />}
            translationKey="freespinsAwarded"
            value={bonusFreespins}
          />
        )}
        {bonusAmount && (
          <TermsPoint
            icon={
              <IconComponent
                className="maxBonus-icon"
                name="maxBonus"
                size="20px"
              />
            }
            translationKey="bonusAmount"
            value={bonusAmount}
          />
        )}
        {spinValue && (
          <TermsPoint
            icon={
              <IconComponent
                className="spinValue-icon"
                name="spinValue"
                size="20px"
              />
            }
            translationKey="spinValue"
            value={spinValue}
          />
        )}
        {maxWin && (
          <TermsPoint
            icon={
              <IconComponent
                className="maxBonus-icon"
                name="maxBonus"
                size="20px"
              />
            }
            translationKey="maxWin"
            value={maxWin}
          />
        )}
        <TermsPoint
          icon={<IconComponent name="maxCashout" size="20px" />}
          translationKey="currency"
          value={bonus.currency}
        />
        {wager && (
          <TermsPoint
            icon={
              <IconComponent className="wager-icon" name="wager" size="20px" />
            }
            translationKey="wager"
            value={wager}
          />
        )}
        {wagerRequirement && (
          <TermsPoint
            icon={
              <IconComponent
                className="maxWinFs-icon"
                name="maxWinFs"
                size="20px"
              />
            }
            translationKey="wagerRequirement"
            value={wagerRequirement}
          />
        )}
        {amountWager && (
          <TermsPoint
            icon={
              <IconComponent
                className="maxWinFs-icon"
                name="maxWinFs"
                size="20px"
              />
            }
            translationKey="amountWager"
            value={amountWager}
          />
        )}
      </ul>
    </ScrollBlock>
  );
};
